import axios from 'axios';
import { getConfig } from '../config';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';

export type SkinAnalysisDemoAnswerValue = string | string[] | number | boolean;

export interface SkinAnalysisDemoAnswer {
  questionId: string;
  label?: string;
  answer: SkinAnalysisDemoAnswerValue;
}

export interface SkinAnalysisDemoAnalyzeRequest {
  surveyUuid: string;
  questionnaireVersionId: number;
  answers: SkinAnalysisDemoAnswer[];
  faceImage: File;
  hairFrontImage?: File;
  hairLeftImage?: File;
  hairRightImage?: File;
}

export interface PhotoGuideItem {
  id: string;
  title: string;
  required: boolean;
  description: string;
  sideId?: number;
}

export interface SkinConcernSignal {
  techName: string;
  label: string;
  value: string | number;
  score: number;
}

export interface HairMetricSignal {
  techName: string;
  label: string;
  value: string;
  confidence: number;
}

export interface RoutineRecommendationItem {
  step: string;
  productName: string;
  category: string;
  matchReason: string;
  searchQuery: string;
}

export interface SkinAnalysisDemoResult {
  analysisId: string;
  questionnaire: {
    surveyUuid: string;
    versionId: number;
    answers: SkinAnalysisDemoAnswer[];
  };
  photoGuidance: {
    required: PhotoGuideItem[];
    recommended: PhotoGuideItem[];
  };
  skinProfile: {
    summary: string;
    skinType: string;
    concerns: SkinConcernSignal[];
  };
  hairProfile: {
    summary: string;
    hairType: string;
    condition: string;
    concerns: string[];
    metrics: HairMetricSignal[];
  };
  recommendations: {
    am: RoutineRecommendationItem[];
    pm: RoutineRecommendationItem[];
    weekly: RoutineRecommendationItem[];
    hair: RoutineRecommendationItem[];
  };
  debug?: unknown;
  raw?: unknown;
}

export class SkinAnalysisDemoClient {
  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;

  constructor() {
    this.baseUrl = getAdminApiUrl(AdminApiDomain.SKIN_ANALYSIS_DEMO);
    this.headers = {
      'X-API-KEY': getConfig().apiKey,
    };
  }

  public async analyze(request: SkinAnalysisDemoAnalyzeRequest): Promise<SkinAnalysisDemoResult> {
    const formData = new FormData();
    formData.append('surveyUuid', request.surveyUuid);
    formData.append('questionnaireVersionId', String(request.questionnaireVersionId));
    formData.append('answers', JSON.stringify(request.answers));
    formData.append('faceImage', request.faceImage);

    this.appendOptionalFile(formData, 'hairFrontImage', request.hairFrontImage);
    this.appendOptionalFile(formData, 'hairLeftImage', request.hairLeftImage);
    this.appendOptionalFile(formData, 'hairRightImage', request.hairRightImage);

    const response = await axios.post<SkinAnalysisDemoResult>(`${this.baseUrl}/skinAnalysisDemo/analyze`, formData, {
      headers: this.headers,
    });
    return response.data;
  }

  private appendOptionalFile(formData: FormData, name: string, file?: File): void {
    if (file) {
      formData.append(name, file);
    }
  }
}
